// The draw.
//
// Five cards at the start of each of your turns, dealt in the reducer rather
// than by the UI — a draw the player has to remember to ask for is a draw that
// gets missed and then reported as cards going missing.
//
// The sample is a seeded Fisher-Yates over instance ids. Never `Math.random`:
// `src/engine/rules/` is deterministic, and a hand that differed between a game and
// its replay is the same class of bug as an unordered map iteration, found the
// same way and much later.

import type { Side } from "../board";
import { areEnemies, pieceIdAt, type Position, royalsOf } from "../position";
import { streamFor } from "../random";
import { RULES } from "../rules";
import { rollPack } from "./loot";
import type { CardInstance, Command, GameState, RunState } from "../commands";

/**
 * Deal this turn's hand.
 *
 * Until the deck passes five cards the hand *is* the deck, and that is fine
 * rather than something to paper over: the sampling machinery is there for the
 * run that goes long and costs nothing while the deck is small.
 */
export function dealHand(run: RunState, seed: number, turn: number): number[] {
  const ids = run.deck.map((entry) => entry.id);
  if (ids.length <= RULES.handSize) return ids;

  const roll = streamFor(seed, turn, 11);
  // Fisher-Yates from the end, so the shuffle is the standard one and its
  // determinism is a property of the stream rather than of the loop.
  for (let i = ids.length - 1; i > 0; i--) {
    const j = Math.floor(roll() * (i + 1));
    [ids[i], ids[j]] = [ids[j], ids[i]];
  }
  return ids.slice(0, RULES.handSize).sort((a, b) => a - b);
}

/**
 * The pack's salt: 13, freed by the Research Station (§31 R1). The deck deals
 * at 11, camp styles at 31, the deposits reserve 81..127.
 */
export const MONARCH_STREAM = 13;

/** The ids of every royal standing for an enemy of `side`, in side then `royalsOf` order. */
function enemyMonarchs(position: Position, side: Side): number[] {
  const out: number[] = [];
  for (let other = 0; other < position.sides; other++) {
    if (!areEnemies(position, side, other as Side)) continue;
    for (const sq of royalsOf(position, other as Side)) {
      const id = sq >= 0 ? pieceIdAt(position, sq) : 0;
      if (id > 0) out.push(id);
    }
  }
  return out;
}

/**
 * **A destroyed enemy Monarch pays a trophy** (§31 A2). One per royal that
 * stood for an enemy of the run's side before the command and stands nowhere
 * after it — by piece id (`Position.pieceId`), so a capture, a turret and a
 * stage's surrender are one rule. Each trophy's `RULES.packSize` cards are
 * rolled **now** (`streamFor`, salted), so re-opening it shows the same three.
 *
 * Credited to the run's side: it is the only side with a hand. **Not when an
 * enemy's own move did it** — a `move` on another army's clock is that army's
 * kill, and the planner has no hand to pay.
 *
 * Mutates `next` (a reducer's output, so a fresh run is written rather than
 * the shared one).
 */
export function payForMonarchs(state: GameState, next: GameState, command: Command): void {
  const run = next.run;
  if (!run || !state.run) return;
  if (command.type === "move" && state.position.turn !== run.side) return;
  const before = enemyMonarchs(state.position, run.side);
  if (before.length === 0) return;
  // Gone from the board, not merely from the royal list: a body still
  // standing somewhere was not destroyed.
  const standing = new Set(next.position.pieceId.values());
  const lost = before.filter((id) => !standing.has(id));
  if (lost.length === 0) return;

  next.run = withTrophies(run, next.seed, lost.length);
}

/**
 * `run` with `count` more trophies, each's three cards rolled now — the one
 * door a card reward is earned by (A2's Monarchs, A11's House of Cards).
 */
export function withTrophies(run: RunState, seed: number, count: number): RunState {
  const trophies = [...(run.trophies ?? [])];
  let nextTrophyId = run.nextTrophyId ?? 0;
  for (let i = 0; i < count; i++) {
    // One stream per trophy, keyed by its id: unique across the run, so two
    // Monarchs falling on one move still roll two different offers.
    trophies.push({ id: nextTrophyId, offer: rollPack(streamFor(seed, nextTrophyId, MONARCH_STREAM)) });
    nextTrophyId++;
  }
  return { ...run, trophies, nextTrophyId };
}

/** Add a card to the deck, and hand back the instance it became. */
export function addCard(run: RunState, card: CardInstance["card"]): CardInstance {
  const instance = { id: run.nextCardId, card };
  run.deck.push(instance);
  run.nextCardId++;
  return instance;
}
